import React, { useState } from "react";
import { PhoneCall, Loader2, ShieldCheck, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Step = ({ n, title, done, children }) => (
  <div className={`rounded-lg border p-2.5 space-y-2 ${done ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
    <div className="text-[11px] font-bold flex items-center gap-1.5">{done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <span className="w-4 h-4 rounded-full bg-secondary text-[9px] grid place-items-center">{n}</span>}{title}</div>
    {!done && children}
  </div>
);

// معالج إضافة رقم عميل: إضافة → رمز تحقق → تسجيل (PIN) → جاهز للربط
export const WhatsAppNumberOnboard = ({ wabas = [], onDone }) => {
  const [f, setF] = useState({ waba_id: wabas[0]?.id || "", cc: "966", phone_number: "", verified_name: "" });
  const [pid, setPid] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [stage, setStage] = useState(0);
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn, next, ok) => {
    setBusy(true);
    try { await fn(); setStage(next); if (ok) toast.success(ok); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const add = () => run(async () => {
    const r = await api.post("/wa/numbers/add", f); setPid(r.data.phone_id);
    if (!r.data.code_sent) toast.warning("أُضيف الرقم لكن تعذر إرسال الرمز: " + (r.data.code_error || "")); 
  }, 1, "أُضيف الرقم وأُرسل رمز التحقق بالرسالة");
  const resend = (method) => run(() => api.post(`/wa/numbers/${pid}/request-code`, { method }), 1, method === "VOICE" ? "سيصلك اتصال بالرمز" : "أُعيد إرسال الرمز");
  const verify = () => run(() => api.post(`/wa/numbers/${pid}/verify-code`, { code }), 2, "تم التحقق من الرقم");
  const register = () => run(async () => { await api.post("/wa/register", { pin, phone_id: pid }); onDone?.(pid); }, 3, "سُجّل الرقم على Cloud API — اربطه الآن ببوت العميل");

  return (
    <div className="rounded-xl border border-dashed border-border p-3 space-y-2 text-xs" data-testid="wa-onboard">
      <div className="font-bold flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5 text-primary" /> إضافة رقم عميل جديد إلى Cloud API</div>
      <div className="text-[10px] text-muted-foreground">قبل البدء: يجب حذف حساب الرقم من تطبيق واتساب/واتساب للأعمال على الجوال، وأن تكون الشريحة متاحة لاستقبال الرمز.</div>
      <Step n={1} title="إضافة الرقم إلى حساب واتساب للأعمال" done={stage >= 1}>
        <div className="grid sm:grid-cols-4 gap-2">
          <select value={f.waba_id} onChange={up("waba_id")} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="wa-onboard-waba">
            {wabas.map((w) => <option key={w.id} value={w.id}>{w.name || "WABA"} · {w.id}</option>)}
          </select>
          <Input value={f.cc} onChange={up("cc")} placeholder="966" className="h-8 text-xs" dir="ltr" data-testid="wa-onboard-cc" />
          <Input value={f.phone_number} onChange={up("phone_number")} placeholder="532556796" className="h-8 text-xs" dir="ltr" data-testid="wa-onboard-number" />
          <Input value={f.verified_name} onChange={up("verified_name")} placeholder="Display name (e.g. Prolancer Support)" className="h-8 text-xs" dir="ltr" data-testid="wa-onboard-name" />
        </div>
        <Button size="sm" className="h-7 text-[11px]" onClick={add} disabled={busy || !f.waba_id || !f.phone_number || !f.verified_name} data-testid="wa-onboard-add">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} إضافة وإرسال الرمز</Button>
      </Step>
      <Step n={2} title="إدخال رمز التحقق الذي وصل إلى الشريحة" done={stage >= 2}>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="h-8 w-32 text-xs" dir="ltr" data-testid="wa-onboard-code" />
          <Button size="sm" className="h-7 text-[11px] gap-1" onClick={verify} disabled={busy || code.length < 6 || !pid} data-testid="wa-onboard-verify"><ShieldCheck className="w-3 h-3" /> تحقق</Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => resend("SMS")} disabled={busy || !pid}>إعادة إرسال SMS</Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => resend("VOICE")} disabled={busy || !pid}>اتصال صوتي</Button>
        </div>
      </Step>
      <Step n={3} title="تسجيل الرقم (رمز PIN من 6 أرقام للتحقق بخطوتين)" done={stage >= 3}>
        <div className="flex items-center gap-2">
          <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN 6 digits" className="h-8 w-32 text-xs" dir="ltr" data-testid="wa-onboard-pin" />
          <Button size="sm" className="h-7 text-[11px] gap-1" onClick={register} disabled={busy || pin.length !== 6 || !pid} data-testid="wa-onboard-register"><KeyRound className="w-3 h-3" /> تسجيل</Button>
        </div>
      </Step>
      {stage >= 3 && <div className="text-emerald-600 font-bold" data-testid="wa-onboard-done">الرقم جاهز (Phone ID {pid}) — اختره الآن في قائمة «ربط» عند بوت العميل.</div>}
    </div>
  );
};
